import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { ErrorHandlerService } from '../../core/services/error-handler.service';
import {
  ExerciseModel,
  ExerciseProgressModel,
  ExerciseRoundModel,
  LessonModel,
  ProblemModel,
  ProblemOptionsModel,
  ProblemProgressModel,
  SmartSavers,
  SolutionModel,
  StrategyResponse,
  StudentRequest,
  StudentSmartSavers,
  TopicModel
} from '@generativ/much-smarter-models';
import { ProblemInfo } from '../lesson/exercise/shared/ProblemInfo';


@Injectable({ providedIn: 'root' })
export class ExerciseService {
  private progressUrl = environment.msStudentApiUrl + 'progress/';
  private smartSaversUrl = environment.msStudentApiUrl + 'smartSavers/';
  private exerciseUrl = environment.msStudentApiUrl + 'exercises/';

  currentExercise: ExerciseModel;
  currentExerciseTime = 0;
  currentExerciseProblemsLength: number;
  currentLesson: LessonModel;
  problemsPending: ProblemModel[];
  exerciseProgressId: number;
  solution: SolutionModel;
  smartSavers: StudentSmartSavers;

  // Variables for saving in localstorage the info of strategy problem
  strategyProblem: ProblemModel;
  strategyProblemTopics: TopicModel[];

  // Persistence round.
  timePerProblemPersistenceRound: number[];
  resultPerProblemPersistenceRound: number[];

  // Previous problem answered
  previousProblemAnswered: ProblemProgressModel[];

  constructor(private http: HttpClient,
              private errorHandlerService: ErrorHandlerService) {
    // Todo: Ana should we save this in localstorage now that we are reloading the exercise when reloading the page?
    this.exerciseProgressId = +localStorage.getItem('exerciseProgressId');
    this.solution = JSON.parse(localStorage.getItem('solution'));
    this.currentLesson = JSON.parse(localStorage.getItem('lesson'));
    this.currentExercise = JSON.parse(localStorage.getItem('currentExercise'));
    this.currentLesson = JSON.parse(localStorage.getItem('currentLesson'));
    this.strategyProblem = JSON.parse(localStorage.getItem('strategyProblem'));
    this.strategyProblemTopics = JSON.parse(localStorage.getItem('strategyProblemTopics'));
    this.problemsPending = JSON.parse(localStorage.getItem('problemsPending'));
    this.smartSavers = JSON.parse(localStorage.getItem('smartSavers'));
    this.timePerProblemPersistenceRound = JSON.parse(localStorage.getItem('timePerProblemPersistenceRound'));
    this.resultPerProblemPersistenceRound = JSON.parse(localStorage.getItem('resultPerProblemPersistenceRound'));
    this.previousProblemAnswered = JSON.parse(localStorage.getItem('previousProblemAnswered'));
    this.currentExerciseProblemsLength = JSON.parse(localStorage.getItem('currentExerciseProblemsLength'));
    this.smartSavers = new StudentSmartSavers();
  }

  getExerciseById(exerciseId: number): Observable<ExerciseModel> {
    return this.http.get<ExerciseModel>(`${environment.msStudentApiUrl}exercises/${exerciseId}`).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  startExercise(exerciseId: number, isPersisted = false): Observable<ExerciseModel> {
    const url = isPersisted ? `startExercisePersistenceRound/${this.exerciseProgressId}/` : `startExerciseRound/${exerciseId}`;

    if (isPersisted) {
      this.timePerProblemPersistenceRound = [];
      this.resultPerProblemPersistenceRound = [];
    }
    return new Observable(observer => {

      this.http.get<ExerciseRoundModel>(this.progressUrl + url).subscribe(
        exerciseRoundModel => {
          this.problemsPending = exerciseRoundModel.exercise.problems;
          this.currentExercise = exerciseRoundModel.exercise;
          // Todo: Read it from the api!
          this.currentExerciseTime = 0;
          this.currentLesson = exerciseRoundModel.exercise.lesson;
          this.currentExerciseProblemsLength = exerciseRoundModel.totalProblemCount;
          this.exerciseProgressId = exerciseRoundModel.exerciseProgressId;
          this.previousProblemAnswered = exerciseRoundModel.previousProblemProgress;
          localStorage.setItem('exerciseProgressId', this.exerciseProgressId.toString());
          localStorage.setItem('currentExercise', JSON.stringify(this.currentExercise));
          localStorage.setItem('currentLesson', JSON.stringify(this.currentLesson));
          localStorage.setItem('problemsPending', JSON.stringify(this.problemsPending));
          localStorage.setItem('previousProblemAnswered', JSON.stringify(this.previousProblemAnswered));
          localStorage.setItem('currentExerciseProblemsLength', JSON.stringify(this.currentExerciseProblemsLength));
          observer.next(exerciseRoundModel.exercise);
          observer.complete();

          // Get SmartSavers

          this.http.get<StudentSmartSavers>(this.smartSaversUrl).subscribe(
            studentSmartSavers => {
              localStorage.setItem('smartSavers', JSON.stringify(studentSmartSavers));
              this.smartSavers.stopTimer = studentSmartSavers.stopTimer;
              this.smartSavers.cutIncorrect = studentSmartSavers.cutIncorrect;
              this.smartSavers.getHint = studentSmartSavers.getHint;
            },
            error => {
              this.errorHandlerService.handleError(error);
            }
          );


        },
        error => {
          this.errorHandlerService.handleError(error);
        }
      );
    });
  }

  getSmartSavers(): StudentSmartSavers {
    return this.smartSavers;
  }

  getCurrentExerciseProblemCount(): number {
    return this.currentExerciseProblemsLength;
  }

  getCurrentExerciseTime(): number {
    return this.currentExerciseTime;
  }

  get problemsPendingLength(): number {
    return this.problemsPending.length;
  }

  getExerciseProgressId(): number {
    return this.exerciseProgressId;
  }

  saveResultPersistenceRound(time: number, result: number) {
    this.timePerProblemPersistenceRound.push(time);
    this.resultPerProblemPersistenceRound.push(result);
    localStorage.setItem('timePerProblemPersistenceRound', JSON.stringify(this.timePerProblemPersistenceRound));
    localStorage.setItem('resultPerProblemPersistenceRound', JSON.stringify(this.resultPerProblemPersistenceRound));
  }

  getCurrentProblem(): Observable<ProblemInfo> {
    const problemInfo = new ProblemInfo();
    const problem = this.problemsPending[0];
    problemInfo.problem = this.createProblemOptionsModel(problem);
    problemInfo.solutionId = problem.solutions[0].id;
    problemInfo.isLastProblem = this.problemsPending.length === 1;
    return of(problemInfo);
  }

  /**
   * After current problem is answered we should update the problem[].
   */
  updateProblemOfExercise() {
    this.problemsPending.splice(0, 1);
  }

  useSmartSaver(type: string) {
    this.http.patch<StudentSmartSavers>(this.smartSaversUrl + type, {}).subscribe(smartSaver => {
      this.smartSavers = smartSaver;
    });
  }


  getExercise(id: number): Observable<ExerciseModel> {
    return this.http.get<ExerciseModel>(this.exerciseUrl + id).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  averageFromArray(array: number[]): number {
    const sum = array.reduce((a, b) => a + b, 0);
    return sum / array.length;
  }

  getIncorrectOptions(): Observable<string[]> {
    const result = JSON.parse(this.solution.wrongAnswers).sort(() => Math.random() - 0.5);
    return of([result[0], result[1]]);
  }

  getHint(): Observable<string> {
    const result = this.problemsPending[0].explanation ? this.problemsPending[0].explanation : '';
    return of(result);
  }

  skipProblem() {
    const currentProblem = this.problemsPending.shift();
    this.problemsPending = [...this.problemsPending, currentProblem];
  }

  get isLastExercise(): boolean {
    const lastExercise = this.currentLesson.exercises[this.currentLesson.exercises.length - 1];
    return this.currentExercise.id === lastExercise.id;
  }

  private createProblemOptionsModel(problem: ProblemModel): ProblemOptionsModel {
    const result = new ProblemOptionsModel();
    result.answers = this.getOptionsFromProblem(problem);
    result.id = problem.id;
    result.content = problem.content;
    result.title = problem.title;
    result.solutionId = problem.solutions[0].id;
    result.passage = problem.passage;
    return result;
  }

  private getOptionsFromProblem(problem: ProblemModel): string[] {
    const solution = problem.solutions[0];
    this.solution = solution;
    let wrongAnswers = [];
    let options = [];
    // Check if solution exists
    if (solution) {
      localStorage.setItem('solution', JSON.stringify(solution));
      wrongAnswers = JSON.parse(solution.wrongAnswers);
      options = [solution.correctAnswer, ...wrongAnswers].sort(() => Math.random() - 0.5);
    }
    return options;
  }

  getInfoForStrategyProblem(id: number): Observable<StrategyResponse> {
    return new Observable(observer => this.http.get<StrategyResponse>(this.progressUrl + 'strategyInfo/' + id).subscribe(
        strategyResponse => {
          this.strategyProblem = strategyResponse.problem;
          this.strategyProblemTopics = strategyResponse.topics;
          if (this.strategyProblem) {
            localStorage.setItem('strategyProblem', JSON.stringify(this.strategyProblem));
          }
          if (this.strategyProblemTopics) {
            localStorage.setItem('strategyProblemTopics', JSON.stringify(this.strategyProblemTopics));
          }
          observer.next(strategyResponse);
          observer.complete();
        },
        error => {
          this.errorHandlerService.handleError(error);
        }
      ));
  }

  // Todo: Check if the id for the exercise is the same .
  getStrategyProblem(): ProblemModel {
    return this.strategyProblem;
  }

  getStrategyTopics(): TopicModel[] {
    return this.strategyProblemTopics;
  }

  identifyTopicForExercise(problemId: number): Observable<number> {
    return this.http.get<number>(this.progressUrl + 'identifyTopic/' + problemId).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  postOpenAnswer(openAnswer: StudentRequest.ExerciseCompleteOpenAnswer): Observable<ExerciseProgressModel> {
    return this.http.post<ExerciseProgressModel>(this.progressUrl + 'exerciseCompleteAnswer/', openAnswer).pipe(
      catchError(this.errorHandlerService.handleError));
  }

  saveUserAnswer(problemProgress: ProblemProgressModel) {
    this.previousProblemAnswered.push(problemProgress);
    localStorage.setItem('previousProblemAnswered', JSON.stringify(this.previousProblemAnswered));
  }

  getPercentageOfProblemCorrect(isPersistence: number): number {
    let problemsSkipped = 0;
    let problemsWrong = 0;
    if (this.previousProblemAnswered && this.previousProblemAnswered.length) {
     for (const problemProgress of this.previousProblemAnswered) {
       if (problemProgress.isPersistence === isPersistence) {
         // In case that the problem was skipped increase the skipped problem account.
         if (problemProgress.smartSaverUsed === SmartSavers.Skip) {
           problemsSkipped ++;
         } else if (problemProgress.isCorrect === 0) {
           problemsWrong ++;
         }
       }
     }
    }
    const totalProblems = this.currentExerciseProblemsLength - problemsSkipped;
    const correctProblems = totalProblems - problemsWrong;

    return Math.round((correctProblems / totalProblems) * 100);
  }

  getExercisePercentageCompleted(): number {
    return Math.round(((this.currentExerciseProblemsLength - this.problemsPending.length) / this.currentExerciseProblemsLength ) * 100);
  }
}
